/**
 * WHICH BACKEND THIS BUNDLE TALKS TO - the single source of truth.
 *
 * Every Supabase client, every `fetch(.../functions/v1/...)` and the
 * environment marker in the admin UI read from here. Nothing else in `src/`
 * may spell out a project URL.
 *
 *   PRODUCTION  main                                      -> sbsniydzogakbgfubgaq
 *   PREVIEW     claude/admin-booking-cancellation-j0zdg1  -> staging project
 *   LOCAL       .env.local                                -> staging project
 *
 * The values come from `VITE_SUPABASE_URL` / `VITE_SUPABASE_ANON_KEY` at build
 * time. The production project remains the FALLBACK when they are absent, for
 * one reason only: production's Netlify site is not reconfigured by this
 * change and must keep working exactly as before. That fallback is the
 * dangerous case for everyone else, so:
 *
 *   - vite.config.ts REFUSES a deploy-preview / branch-deploy build whose
 *     variables are missing or point at production (see checkFrontendBuildTarget),
 *   - the admin UI shows a PRODUCTION / STAGING marker outside the production
 *     deploy context (see components/admin/EnvironmentBadge),
 *   - `SUPABASE_URL_FROM_ENV` lets diagnostics say when the fallback carried
 *     the app.
 *
 * Both values are public by design (the anon key is shipped in every bundle);
 * the service-role key and provider secrets exist only inside Edge Functions.
 *
 * See docs/ENVIRONMENTS.md.
 */

/** The live project ref. Must match scripts/lib/environmentGuard.mjs. */
export const PRODUCTION_PROJECT_REF = 'sbsniydzogakbgfubgaq';

const PRODUCTION_URL = `https://${PRODUCTION_PROJECT_REF}.supabase.co`;
const PRODUCTION_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNic25peWR6b2dha2JnZnViZ2FxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjQxNDUzODksImV4cCI6MjA3OTcyMTM4OX0.fXg8U94S-hC4_eT1s9uedhA_tIA0Pzw7e_NYDSCUsMY';

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

const envUrl = import.meta.env.VITE_SUPABASE_URL;
const envAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** True when the deployment supplied a URL; false when the fallback carries the app. */
export const SUPABASE_URL_FROM_ENV: boolean = nonEmpty(envUrl);

export const SUPABASE_URL: string = SUPABASE_URL_FROM_ENV ? envUrl.trim() : PRODUCTION_URL;

/**
 * The anon key follows the URL: a URL from the environment with no matching
 * key is a misconfiguration, and pairing it with the production key would
 * only produce confusing 401s against the wrong project.
 */
export const SUPABASE_ANON_KEY: string = SUPABASE_URL_FROM_ENV
  ? (nonEmpty(envAnonKey) ? envAnonKey.trim() : '')
  : PRODUCTION_ANON_KEY;

/** Extracts `<ref>` from `https://<ref>.supabase.co`, or null for anything else. */
export function projectRefFromUrl(url: string): string | null {
  const m = /^https?:\/\/([a-z0-9]{20})\.supabase\.(co|in)\b/i.exec(url.trim());
  return m ? m[1].toLowerCase() : null;
}

/** The project this bundle talks to, or null for a non-Supabase URL (local stack). */
export const BACKEND_PROJECT_REF: string | null = projectRefFromUrl(SUPABASE_URL);

export const BACKEND_IS_PRODUCTION: boolean = BACKEND_PROJECT_REF === PRODUCTION_PROJECT_REF;

/**
 * Netlify deploy context baked in at build time by vite.config.ts:
 * `production`, `deploy-preview`, `branch-deploy`, `dev`, `unknown` or `local`.
 */
declare const __DEPLOY_CONTEXT__: string;
export const DEPLOY_CONTEXT: string =
  typeof __DEPLOY_CONTEXT__ === 'string' ? __DEPLOY_CONTEXT__ : 'local';

/**
 * The environment marker is shown everywhere EXCEPT the production deploy
 * context. A production build therefore never renders it, whatever backend it
 * points at; a preview or local build always says which backend it is on.
 */
export const SHOW_ENVIRONMENT_MARKER: boolean = DEPLOY_CONTEXT !== 'production';

export type BackendLabel = 'PRODUCTION' | 'STAGING' | 'LOCAL';

/** Human label for the backend, used by the marker and by diagnostics. */
export function describeBackend(): { label: BackendLabel; ref: string; fromEnv: boolean } {
  if (BACKEND_IS_PRODUCTION) {
    return { label: 'PRODUCTION', ref: PRODUCTION_PROJECT_REF, fromEnv: SUPABASE_URL_FROM_ENV };
  }
  if (BACKEND_PROJECT_REF) {
    return { label: 'STAGING', ref: BACKEND_PROJECT_REF, fromEnv: SUPABASE_URL_FROM_ENV };
  }
  return { label: 'LOCAL', ref: SUPABASE_URL, fromEnv: SUPABASE_URL_FROM_ENV };
}
